import { groupBy, values } from 'lodash';

import { isAddressEmpty } from '~address-form/utils/address-utils';

import { Choice, Session, StateResponse } from '../../api/api';

function matchSessions(newState: StateResponse, oldState: StateResponse) {
  // here the first item refers to new state, as per lodash manual.
  const matchedSessions = values(
    groupBy([...newState.sessions, ...oldState.sessions], (session: Session) => session.session_id)
  );

  // cart might get added or removed, we never know.
  return matchedSessions.filter(sessions => sessions.length === 2);
}

export function sessionIncludesChoice(session: Session, choice: Choice) {
  const category = session.categories.find(({ id }) => id === choice.shipping_category);
  if (!category) {
    return false;
  }

  return category.options.some(option => {
    return (
      option.shipping_method === choice.shipping_method &&
      /**
       * Empty location matches any location, because it cannot be considered a
       * lost choice if we go from empty location to a specific location.
       */
      (choice.location_ref === undefined || option.location?.id === choice.location_ref)
    );
  });
}

export function compareNewAndOldSessionsById(newState: StateResponse, oldState: StateResponse) {
  if (newState.purchase_country !== oldState.purchase_country) {
    /**
     * Purchase country could be changed by merchant through external API
     */
    return false;
  }

  /**
   * Since state could have multiple sessions, we need to compare each one
   * of them for similarity with previous state. We're storing "similarity" state
   *  between every old and new session in array.
   */
  const similarCategories = newState.sessions.map((newSession: Session) => {
    const oldSession = oldState.sessions.find(
      ({ session_id }) => session_id === newSession.session_id
    );

    if (!oldSession) return false;

    return isCurrentCategorySimilar(newSession, oldSession);
  });

  /**
   * Return true if category has been lost
   */
  return similarCategories.some(item => !item);
}

export function isCurrentCategorySimilar(newSession: Session, oldSession: Session) {
  /**
   * We define similar category as a category with the same group_id
   * (even if the region has changed). The shipping method could differ.
   */

  const choice = newSession.user_choice || newSession.preselected_choice;
  const selectedCategory = newSession.categories.find(({ id }) => id === choice.shipping_category);

  if (!selectedCategory) {
    return false;
  }

  const oldChoice = oldSession.user_choice || oldSession.preselected_choice;
  const oldCategory = oldSession.categories.find(({ id }) => id === oldChoice.shipping_category);

  if (
    !!oldCategory?.group_id &&
    !!selectedCategory.group_id &&
    oldCategory.group_id === selectedCategory.group_id
  ) {
    return true;
  }
  return false;
}

/**
 * Return "true" if category is lost on any session.
 */
export function checkCategoryLost(newState: StateResponse, oldState: StateResponse) {
  if (newState.purchase_country !== oldState.purchase_country) {
    /*
     * Edge case: Some merchant use country selector on checkout page and
     * update purchase_country on the fly. In such case delivery options will
     * flush, so suppress error message.
     */
    return false;
  }

  return matchSessions(newState, oldState).some(([newSession, oldSession]) => {
    if (
      isAddressEmpty(oldState.search_address) &&
      !oldSession.flags.show_shipping_categories_before_address
    ) {
      return false;
    }

    if (!oldSession.user_choice && !newSession.user_choice) {
      /**
       * Edge case: in some circumstances there is possibility that
       * session will be updated by merchant during user visit on the
       * checkout page. HM/Centra is leveraging session.update call
       * to pass some additional attributes to the session, what results in
       * additional call to HM's LMX ECO to retrieve possible delivery options.
       * Combined with firebase messaging, this could result in triggering
       * the pop up before user even arrives to delivery options section,
       * resulting in bad UX.
       */
      return false;
    }

    const oldChoice = oldSession.user_choice || oldSession.preselected_choice;

    // check only category
    return newSession.categories.every(category => category.id !== oldChoice.shipping_category);
  });
}

export function checkChoiceLost(newState: StateResponse, oldState: StateResponse) {
  if (newState.purchase_country !== oldState.purchase_country) {
    /*
     * Edge case: Some merchant use country selector on checkout page and
     * update purchase_country on the fly. In such case delivery options will
     * flush, so suppress error message.
     */
    return false;
  }

  return matchSessions(newState, oldState).some(([newSession, oldSession]) => {
    const oldChoice = oldSession.user_choice || oldSession.preselected_choice;

    return !sessionIncludesChoice(newSession, oldChoice);
  });
}
